/**
 * Animated close for elements that exit via CSS transitions.
 *
 * Adds .is-closing to run exit transitions before calling onClose().
 * Works around Safari not supporting allow-discrete on display/overlay
 * transitions.
 *
 * @param {HTMLElement} element - The element to close
 * @param {object} [opts]
 * @param {HTMLElement} [opts.transitionTarget] - Element to listen for transitionend on.
 *   Defaults to element itself. Drawers pass .drawer-panel since the slide
 *   transition runs on the panel, not the dialog.
 * @param {function} [opts.onClose] - Cleanup callback. Defaults to element.close()
 *   for native <dialog> elements.
 * @param {number} [opts.timeout] - Fallback timeout in ms if transitionend doesn't fire. Default 300.
 */
export function animatedClose(element, opts = {}) {
    const { transitionTarget, onClose, timeout = 300 } = opts;
    if (element.classList.contains('is-closing')) return;
    if ('open' in element && !element.open) return;

    element.classList.add('is-closing');

    const target = transitionTarget || element;
    const close = onClose || (() => 'close' in element && element.close());

    let finished = false;
    const timer = setTimeout(done, timeout);

    // Whichever arrives first wins: the transition end or the timeout
    // fallback (for transitions that never fire, e.g. under
    // prefers-reduced-motion). The guard makes double invocation
    // impossible; the loser is a no-op.
    function done() {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        element.classList.remove('is-closing');
        close();
    }

    target.addEventListener('transitionend', done, { once: true });
}
